import styled, { css } from 'styled-components';

export const PronunciationLayout = styled.div(
  ({ theme }) => css`
    width: min(100vw, ${theme.sz.md});
    min-height: min(calc(100dvh - ${theme.sz.s16}), ${theme.sz.ms});
    display: grid;
    align-content: start;
    grid-template-columns: 1fr;
    grid-template-rows:
      /* Progress indicator, plus the free-words pill when shown */
      auto
      /* Dynamic height rows to adapt to varying text content */
      auto auto
      /* Dynamic height row for the primary microphone action, with its hint
         text or skip button directly beneath it */
      auto
      /* Fixed height row for the secondary audio/video actions */
      ${theme.sz.s16};
    gap: ${theme.sz.s6};
    justify-items: center;

    @media (max-width: ${theme.sz.md}) {
      padding-top: ${theme.sz.s4};
      min-height: min(calc(100dvh - ${theme.sz.s12}), ${theme.sz.lg});
      gap: ${theme.sz.s4};

      @media (max-height: ${theme.sz.xl}) {
        grid-template-rows:
          /* Progress indicator, plus the free-words pill when shown */
          auto
          /* Dynamic height rows to adapt to varying text content */
          auto auto
          /* Growing rows for the microphone and secondary actions */
          auto auto;
      }
    }
  `,
);

export const AnalysisLayout = styled.div(
  ({ theme }) => css`
    max-width: ${theme.sz.xs};
    display: grid;
    grid-template-columns: 1fr;
    gap: ${theme.sz.s12};
    justify-items: center;

    @media (max-width: ${theme.sz.md}) {
      gap: ${theme.sz.s4};
    }

    @media (min-height: ${theme.sz.xl}) {
      min-height: ${theme.sz.sm};
      align-items: center;
    }
  `,
);

export const FullRowContainer = styled.div<{ $wide?: boolean }>(
  ({ $wide }) => css`
    grid-column: 1 / -1;

    ${
      $wide &&
      css`
        width: 100%;
      `
    }
  `,
);

export const HiddenContent = styled.div<{ $isVisible?: boolean }>(
  ({ $isVisible }) => css`
    position: relative;

    ${
      !$isVisible &&
      css`
        > *:not(${OverrideContent}) {
          visibility: hidden;
        }
      `
    }
  `,
);

export const OverrideContent = styled.div(
  () => css`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
);

export const CompactLayout = styled.div(
  ({ theme }) => css`
    width: 100%;
    padding: ${theme.sz.s4};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s4};
  `,
);

export const CompactRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s4};
  `,
);

export const CompactContinueButtonsContainer = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
  `,
);

export const ContinueButtonsContainer = styled(FullRowContainer)(
  ({ theme }) => css`
    min-width: ${theme.sz.xs};
    min-height: ${theme.sz.s32};
    display: flex;
    flex-direction: column;
    justify-content: end;

    @media (max-width: ${theme.sz.ms}) {
      min-width: unset;
      width: 100%;
    }
  `,
);

// Two-column session layout: the native speaker video sits beside the target
// word and microphone, so the learner can watch and record without leaving
// the screen. Collapses to a single column when there is no room for both.
export const SessionLayout = styled.div(
  ({ theme }) => css`
    width: min(100%, ${theme.sz.xxl});
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-content: start;
    align-items: center;
    gap: ${theme.sz.s8} ${theme.sz.s16};

    @media (max-width: ${theme.sz.xl}) {
      grid-template-columns: 1fr;
      width: min(100%, ${theme.sz.md});
      gap: ${theme.sz.s4};
    }
  `,
);

export const VideoColumn = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.ms};
    justify-self: end;

    @media (max-width: ${theme.sz.xl}) {
      justify-self: center;
    }
  `,
);

export const PromptColumn = styled.div(
  ({ theme }) => css`
    width: 100%;
    max-width: ${theme.sz.sm};
    justify-self: start;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.sz.s6};

    @media (max-width: ${theme.sz.xl}) {
      justify-self: center;
      gap: ${theme.sz.s4};
    }
  `,
);
